import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import type { TextFieldProps } from '@mui/material/TextField';

import { PasswordTextField } from './PasswordTextField.js';

interface RHFPasswordFieldProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<TextFieldProps, 'name' | 'type' | 'onCopy'> {
  name: Path<TFieldValues>;
  helperText?: string;
  hideErrorMessage?: boolean;
  allowCopy?: boolean;
}

export function RHFPasswordField<TFieldValues extends FieldValues = FieldValues>({
  name,
  helperText,
  hideErrorMessage = false,
  allowCopy = false,
  onBlur,
  ...other
}: RHFPasswordFieldProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const value = field.value as string;
        const handleCopy =
          allowCopy && value
            ? () =>
                navigator.clipboard.writeText(value).then(() => toast.info('Copied to clipboard'))
            : undefined;

        return (
          <PasswordTextField
            {...field}
            fullWidth
            error={!!error}
            helperText={hideErrorMessage ? helperText ?? '' : error?.message ?? helperText}
            value={value}
            onBlur={(e) => {
              field.onBlur();
              onBlur?.(e);
            }}
            onCopy={handleCopy}
            {...other}
          />
        );
      }}
    />
  );
}
